import { useEffect, useMemo, useRef, useState } from "react";
import { gsap } from "gsap";
import {
  AlertTriangle,
  Ear,
  MessageCircle,
  Send,
  ShieldCheck,
  Sparkles,
  Wand2
} from "lucide-react";
import fullBodyLayer from "../assets/master-ren-rig/01-full-body-cutout.png";
import haloLayer from "../assets/master-ren-rig/09-ink-halo-background-strokes.png";
import "./MasterRenStage.css";

type RenState = "idle" | "listening" | "thinking" | "speaking" | "warning";

type HermesData = {
  answer: string;
  mode: "public" | "personal" | "review";
  safety_labels: string[];
  used_private_context: boolean;
  next_action?: string;
};

type HermesEnvelope =
  | { ok: true; data: HermesData; meta: { correlation_id: string } }
  | { ok: false; error: { code: string; message: string; category: string }; meta: { correlation_id: string } };

const introText =
  "من مستر رن هستم. قبل از معامله، مسیرت را روشن کنیم. امروز دنبال چه چیزی هستی؟";

const quickPrompts = [
  "مسیر شروع من را بساز",
  "اشتباهات ترید من را پیدا کن",
  "اول PRISM را انجام بدهم؟"
];

const stateLabels: Array<{ state: RenState; label: string }> = [
  { state: "idle", label: "آرام" },
  { state: "listening", label: "شنیدن" },
  { state: "thinking", label: "فکر" },
  { state: "speaking", label: "راهنمایی" },
  { state: "warning", label: "هشدار" }
];

function wait(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function isRiskPrompt(prompt: string, labels: string[] = []) {
  return (
    prompt.includes("سیگنال") ||
    prompt.includes("ریسک") ||
    prompt.includes("خرید") ||
    prompt.includes("فروش") ||
    labels.some((label) => label.includes("risk") || label.includes("signal"))
  );
}

function buildMentorReply(prompt: string) {
  if (isRiskPrompt(prompt)) {
    return "اینجا توقف می‌کنیم. من سیگنال قطعی نمی‌دهم. اول ریسک، دلیل ورود و سناریوی خروج را روشن کن؛ بعد تصمیم بگیر.";
  }

  if (prompt.includes("PRISM")) {
    return "اگر هنوز PRISM نداده‌ای، از همان شروع کن. اول الگوی فشار و عجله تو را می‌خوانم، بعد مسیر آکادمی و تمرین را دقیق‌تر می‌کنم.";
  }

  if (prompt.includes("اشتباه")) {
    return "برای پیدا کردن اشتباه، سه چیز لازم دارم: دلیل ورود، احساس لحظه تصمیم، و نتیجه معامله. یکی از آخرین معاملاتت را کوتاه توضیح بده.";
  }

  return "مسیر پیشنهادی من این است: اول PRISM، بعد یک درس کوتاه آکادمی، سپس ژورنال یک تصمیم واقعی. امروز فقط یک قدم روشن انتخاب می‌کنیم.";
}

async function requestHermes(message: string, signal: AbortSignal) {
  const apiBaseUrl = import.meta.env.VITE_HERMES_API_BASE_URL ?? "";
  const response = await fetch(`${apiBaseUrl}/api/hermes/public-chat`, {
    body: JSON.stringify({ message, locale: "fa" }),
    headers: { "Content-Type": "application/json" },
    method: "POST",
    signal
  });

  const envelope = (await response.json()) as HermesEnvelope;

  if (!response.ok || !envelope.ok) {
    throw new Error(envelope.ok ? "hermes_http_error" : envelope.error.code);
  }

  return envelope.data;
}

function useTypedText(text: string, speed = 28, enabled = true) {
  const [typed, setTyped] = useState("");

  useEffect(() => {
    setTyped("");
    if (!enabled) return;

    let index = 0;
    const timer = window.setInterval(() => {
      index += 1;
      setTyped(text.slice(0, index));
      if (index >= text.length) {
        window.clearInterval(timer);
      }
    }, speed);

    return () => window.clearInterval(timer);
  }, [enabled, speed, text]);

  return typed;
}

export function MasterRenStage() {
  const stageRef = useRef<HTMLElement | null>(null);
  const rigRef = useRef<HTMLDivElement | null>(null);
  const noteRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [question, setQuestion] = useState("");
  const [activeReply, setActiveReply] = useState("");
  const [introReady, setIntroReady] = useState(false);
  const [renState, setRenState] = useState<RenState>("idle");
  const [replySource, setReplySource] = useState<"intro" | "hermes" | "local">("intro");
  const typedIntro = useTypedText(introText, 28, introReady);
  const typedReply = useTypedText(activeReply, activeReply ? 15 : 28);

  const statusLabel = useMemo(() => {
    if (renState === "thinking") return "هرمس در حال فکر کردن";
    if (renState === "speaking") return replySource === "hermes" ? "هرمس پاسخ می‌دهد" : "راهنمایی نمونه";
    if (renState === "warning") return "هشدار ریسک";
    if (renState === "listening") return "مستر رن توجه می‌کند";
    return "آماده شنیدن";
  }, [renState, replySource]);

  useEffect(() => {
    const stage = stageRef.current;
    const rig = rigRef.current;
    const note = noteRef.current;
    if (!stage || !rig || !note) return;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const ctx = gsap.context(() => {
      gsap.set(
        [
          ".ren-stage-copy",
          rig,
          ".ren-rig-layer",
          ".ren-sigil",
          ".ren-ink-mark",
          ".ren-note-ink",
          ".ren-ink-particle",
          ".ren-brush-stroke",
          ".ren-cinema-beam",
          ".ren-eye-glint",
          ".ren-eye-focus",
          ".ren-eye-shadow"
        ],
        { autoAlpha: reduceMotion ? 1 : 0 }
      );
      gsap.set(note, { autoAlpha: reduceMotion ? 1 : 0, scaleY: reduceMotion ? 1 : 0.04 });
      gsap.set(".ren-eyelid", { autoAlpha: 0, scaleY: 0.08, transformOrigin: "center" });

      if (reduceMotion) {
        setIntroReady(true);
        return;
      }

      const intro = gsap.timeline({
        defaults: { duration: 0.72, ease: "power3.out" }
      });

      intro
        .fromTo(".ren-cinema-beam", { autoAlpha: 0, scaleX: 0.15 }, { autoAlpha: 0.65, scaleX: 1, duration: 0.9 }, 0)
        .fromTo(".ren-brush-stroke", { autoAlpha: 0, scale: 0.42, rotation: -70 }, { autoAlpha: 0.7, scale: 1, rotation: 18, duration: 0.82 }, 0.06)
        .fromTo(".ren-halo-art", { autoAlpha: 0, scale: 0.58, rotation: -32 }, { autoAlpha: 0.76, scale: 1.08, rotation: 0 }, 0.18)
        .fromTo(rig, { autoAlpha: 0, y: 28, scale: 0.98 }, { autoAlpha: 1, y: 0, scale: 1, duration: 0.82 }, 0.28)
        .fromTo(".ren-rig-layer", { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.44 }, 0.24)
        .fromTo(".ren-stage-copy", { autoAlpha: 0, y: 24 }, { autoAlpha: 1, y: 0 }, 0.46)
        .to(note, { autoAlpha: 1, scaleY: 1, y: 0, rotationX: 0, duration: 0.62 }, 1.08)
        .fromTo(".ren-note-ink", { autoAlpha: 0, scaleX: 0 }, { autoAlpha: 0.9, scaleX: 1, duration: 0.52 }, 1.24)
        .fromTo(".ren-ink-mark", { autoAlpha: 0, scaleX: 0, transformOrigin: "right center" }, { autoAlpha: 0.7, scaleX: 1, stagger: 0.12 }, 0.2)
        .fromTo(".ren-sigil", { autoAlpha: 0, scale: 0.82 }, { autoAlpha: 1, scale: 1, stagger: 0.1 }, 0.64)
        .call(() => setIntroReady(true), [], 1.58)
        .call(() => {
          gsap.set([".ren-stage-copy", rig, ".ren-rig-layer", note], { autoAlpha: 1 });
        }, [], 1.92);

      const blink = gsap.timeline({ repeat: -1, repeatDelay: 3.4 });
      blink
        .to(".ren-eyelid", { autoAlpha: 0.82, scaleY: 1, duration: 0.075, ease: "power1.out" })
        .to(".ren-eyelid", { autoAlpha: 0, scaleY: 0.08, duration: 0.11, ease: "power1.in" }, "+=0.04")
        .to(".ren-eyelid", { autoAlpha: 0.65, scaleY: 0.9, duration: 0.06, ease: "power1.out" }, "+=0.22")
        .to(".ren-eyelid", { autoAlpha: 0, scaleY: 0.08, duration: 0.1, ease: "power1.in" }, "+=0.03");

      const eyeX = gsap.quickTo(".ren-eye-glint", "x", { duration: 0.32, ease: "power3.out" });
      const eyeY = gsap.quickTo(".ren-eye-glint", "y", { duration: 0.32, ease: "power3.out" });
      const onPointerMove = (event: PointerEvent) => {
        const rect = stage.getBoundingClientRect();
        const nx = (event.clientX - rect.left) / rect.width - 0.5;
        const ny = (event.clientY - rect.top) / rect.height - 0.5;
        eyeX(nx * 7);
        eyeY(ny * 4);
      };

      stage.addEventListener("pointermove", onPointerMove);
      return () => stage.removeEventListener("pointermove", onPointerMove);
    }, stage);

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    const ctx = gsap.context(() => {
      gsap.to(".ren-eye-glint", {
        autoAlpha: renState === "warning" ? 0.15 : renState === "thinking" ? 0.38 : 0.58,
        scale: renState === "listening" ? 1.22 : renState === "warning" ? 0.72 : 1,
        duration: 0.28,
        ease: "power2.out",
        overwrite: "auto"
      });

      gsap.to(".ren-eye-focus", {
        autoAlpha: renState === "listening" || renState === "thinking" ? 0.72 : 0,
        scaleX: renState === "thinking" ? 1.12 : 1,
        duration: 0.26,
        ease: "power2.out",
        overwrite: "auto"
      });

      gsap.to(".ren-eye-shadow", {
        autoAlpha: renState === "warning" ? 0.76 : renState === "thinking" ? 0.34 : 0,
        scaleY: renState === "warning" ? 1 : 0.55,
        duration: renState === "warning" ? 0.18 : 0.34,
        ease: "power2.out",
        overwrite: "auto"
      });

      gsap.to(".ren-eyelid", {
        autoAlpha: renState === "warning" ? 0.55 : renState === "thinking" ? 0.18 : 0,
        scaleY: renState === "warning" ? 0.58 : 0.16,
        duration: 0.22,
        ease: "power2.out",
        overwrite: "auto"
      });

      gsap.to(".ren-note", {
        borderColor: renState === "warning" ? "rgba(149, 31, 36, 0.62)" : "rgba(160, 109, 56, 0.34)",
        boxShadow:
          renState === "warning"
            ? "0 28px 90px rgba(105, 19, 22, 0.24)"
            : "0 28px 80px rgba(64, 48, 31, 0.2)",
        duration: 0.25,
        overwrite: "auto"
      });

      gsap.to(".ren-stage", {
        "--warningGlow": renState === "warning" ? 1 : 0,
        duration: 0.25,
        overwrite: "auto"
      });

      const stateLoop = gsap.timeline({ repeat: -1, yoyo: true });

      if (renState === "listening") {
        stateLoop
          .to(".ren-eye-glint", { x: -4, y: -1, autoAlpha: 0.75, duration: 0.7, ease: "sine.inOut" }, 0)
          .to(".ren-eye-focus", { scaleX: 1.18, autoAlpha: 0.8, duration: 0.7, ease: "sine.inOut" }, 0);
      }

      if (renState === "thinking") {
        stateLoop
          .to(".ren-eye-glint", { x: 3, y: -2, autoAlpha: 0.5, duration: 0.9, ease: "sine.inOut" }, 0)
          .to(".ren-eye-focus", { scaleX: 1.24, autoAlpha: 0.84, duration: 0.9, ease: "sine.inOut" }, 0)
          .to(".ren-eye-shadow", { autoAlpha: 0.46, scaleY: 0.68, duration: 0.9, ease: "sine.inOut" }, 0);
      }

      if (renState === "speaking") {
        stateLoop
          .to(".ren-eye-glint", { x: -2, y: 1, autoAlpha: 0.64, duration: 0.34, ease: "sine.inOut" }, 0)
          .to(".ren-eyelid", { autoAlpha: 0.12, scaleY: 0.2, duration: 0.34, ease: "sine.inOut" }, 0);
      }

      if (renState === "warning") {
        stateLoop
          .to(".ren-eye-shadow", { autoAlpha: 0.86, scaleY: 1.1, duration: 0.36, ease: "power2.inOut" }, 0)
          .to(".ren-eyelid", { autoAlpha: 0.66, scaleY: 0.7, duration: 0.36, ease: "power2.inOut" }, 0);
      }

      return () => stateLoop.kill();
    }, stage);

    return () => ctx.revert();
  }, [renState]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || !question) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".ren-eye-focus",
        { autoAlpha: 0.18, scaleX: 0.72 },
        { autoAlpha: 0.82, scaleX: 1.1, duration: 0.18, ease: "power2.out", overwrite: "auto" }
      );
    }, stage);

    return () => ctx.revert();
  }, [question]);

  async function submitPrompt(prompt = question) {
    const cleanPrompt = (prompt || inputRef.current?.value || "").trim();
    if (!cleanPrompt || renState === "thinking") return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setQuestion(cleanPrompt);
    setActiveReply("");
    setReplySource("hermes");
    setRenState("thinking");

    const startedAt = Date.now();

    try {
      const response = await requestHermes(cleanPrompt, controller.signal);
      await wait(Math.max(650 - (Date.now() - startedAt), 0));
      if (controller.signal.aborted) return;
      setActiveReply(response.answer);
      setReplySource("hermes");
      setRenState(isRiskPrompt(cleanPrompt, response.safety_labels) ? "warning" : "speaking");
      setQuestion("");
    } catch {
      if (controller.signal.aborted) return;
      await wait(Math.max(650 - (Date.now() - startedAt), 0));
      setActiveReply(buildMentorReply(cleanPrompt));
      setReplySource("local");
      setRenState(isRiskPrompt(cleanPrompt) ? "warning" : "speaking");
      setQuestion("");
    }
  }

  return (
    <section
      id="top"
      ref={stageRef}
      className={`ren-stage ren-state-${renState}`}
      data-ren-state={renState}
      aria-label="Master Ren live mentor"
    >
      <div className="ren-vignette" aria-hidden="true" />
      <div className="ren-paper-grain" aria-hidden="true" />
      <div className="ren-cinema-beam" aria-hidden="true" />
      <div className="ren-ink-mark ren-ink-mark-one" aria-hidden="true" />
      <div className="ren-ink-mark ren-ink-mark-two" aria-hidden="true" />
      <span className="ren-ink-particle ren-ink-particle-one" aria-hidden="true" />
      <span className="ren-ink-particle ren-ink-particle-two" aria-hidden="true" />
      <span className="ren-ink-particle ren-ink-particle-three" aria-hidden="true" />

      <div className="ren-stage-interaction">
        <div className="ren-stage-copy">
          <p className="section-label">مسیر رشد معامله‌گر ایرانی</p>
          <h1>مستر رن اول گوش می‌دهد، بعد مسیر می‌سازد.</h1>
          <p className="ren-stage-lede">
            اینجا صفحه اول فقط معرفی نیست؛ یک نقطه شروع زنده است. خواسته‌ات را بگو تا هرمس،
            PRISM و آکادمی مسیر بعدی را روشن کنند.
          </p>
          <div className="ren-stage-badges" aria-label="Master Ren capabilities">
            <span><MessageCircle size={16} /> گفت‌وگوی زنده</span>
            <span><Wand2 size={16} /> راهنمایی مرحله‌ای</span>
            <span><ShieldCheck size={16} /> بدون وعده سود</span>
          </div>
          <div className="ren-motion-controls" aria-label="تست حالت‌های مستر رن">
            {stateLabels.map((item) => (
              <button
                className={renState === item.state ? "active" : ""}
                key={item.state}
                type="button"
                onClick={() => setRenState(item.state)}
              >
                {item.state === "listening" && <Ear size={14} />}
                {item.state === "warning" && <AlertTriangle size={14} />}
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <aside ref={noteRef} className="ren-note" aria-label="Master Ren note">
          <span className="ren-note-ink" aria-hidden="true" />
          <div className="ren-note-header">
            <span className="ren-status-dot" />
            <span>{statusLabel}</span>
            <Sparkles size={17} aria-hidden="true" />
          </div>
          <div className="ren-note-body">
            <p>{typedReply || typedIntro}<span className="typing-caret" /></p>
            {renState === "thinking" && (
              <div className="ren-thinking-copy" role="status">
                مستر رن دارد زمینه را می‌خواند...
              </div>
            )}
          </div>
          <form
            className="ren-prompt-form"
            onSubmit={(event) => {
              event.preventDefault();
              submitPrompt();
            }}
          >
            <input
              ref={inputRef}
              value={question}
              onBlur={() => {
                if (!activeReply && renState === "listening") setRenState("idle");
              }}
              onChange={(event) => {
                setQuestion(event.target.value);
                if (renState === "idle" || renState === "speaking" || renState === "warning") {
                  setRenState("listening");
                }
              }}
              onFocus={() => {
                if (renState === "idle") setRenState("listening");
              }}
              placeholder="مثلا: نمی‌دانم از کجا شروع کنم"
              aria-label="درخواست از مستر رن"
            />
            <button type="submit" aria-label="ارسال درخواست">
              <Send size={18} />
            </button>
          </form>
          <div className="ren-quick-prompts" aria-label="پیشنهادهای سریع">
            {quickPrompts.map((prompt) => (
              <button type="button" key={prompt} onClick={() => submitPrompt(prompt)}>
                {prompt}
              </button>
            ))}
          </div>
        </aside>
      </div>

      <div className="ren-character-shell" id="hermes">
        <div ref={rigRef} className="ren-rig" aria-label="Master Ren animated mentor">
          <span className="ren-brush-stroke" aria-hidden="true" />
          <img className="ren-rig-layer ren-halo-art" src={haloLayer} alt="" aria-hidden="true" />
          <img className="ren-rig-layer ren-body-base" src={fullBodyLayer} alt="Master Ren" />
          <span className="ren-eye-focus" aria-hidden="true" />
          <span className="ren-eye-shadow" aria-hidden="true" />
          <span className="ren-eye-glint ren-eye-glint-left" aria-hidden="true" />
          <span className="ren-eye-glint ren-eye-glint-right" aria-hidden="true" />
          <span className="ren-eyelid ren-eyelid-left" aria-hidden="true" />
          <span className="ren-eyelid ren-eyelid-right" aria-hidden="true" />
        </div>
        <div className="ren-sigil ren-sigil-one" aria-hidden="true">Observe</div>
        <div className="ren-sigil ren-sigil-two" aria-hidden="true">Discipline</div>
      </div>
    </section>
  );
}
